import { useState } from "react";
import { CalendarBlank, X } from "@phosphor-icons/react";
import { format, parseISO } from "date-fns";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";

/**
 * Standardized date filter button backed by shadcn Calendar + Popover.
 *
 * Props:
 *   label     — eyebrow label shown above the trigger
 *   value     — ISO date string (YYYY-MM-DD) or empty
 *   onChange  — (isoString | "") => void
 *   placeholder — shown when value is empty (default "Pick a date")
 *   testid    — applied to the trigger button
 *
 * Keeps the same contract as the legacy `<Input type="date" />` filter
 * buttons on TDS/GST/Forecast/Collections pages — emits an ISO date string
 * OR an empty string when cleared. No date library lock-in beyond
 * `date-fns` (already a transitive dep via `react-day-picker`).
 */
export default function DateFilterButton({ label, value, onChange, placeholder = "Pick a date", testid }) {
    const [open, setOpen] = useState(false);
    const parsed = value ? parseISO(value) : null;

    const pick = (date) => {
        if (!date) {
            onChange("");
            setOpen(false);
            return;
        }
        // Emit ISO YYYY-MM-DD in local time (matches the contract of native <input type="date">)
        const iso = format(date, "yyyy-MM-dd");
        onChange(iso);
        setOpen(false);
    };

    return (
        <div>
            {label && <Label className="text-[10px] font-semibold text-slate-500 uppercase">{label}</Label>}
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        type="button"
                        variant="outline"
                        className={cn(
                            "mt-1 h-8 w-full px-2 text-xs font-normal justify-start text-left gap-1.5",
                            !parsed && "text-slate-400",
                        )}
                        data-testid={testid}
                    >
                        <CalendarBlank size={13} className="shrink-0" />
                        <span className="flex-1 font-mono">
                            {parsed ? format(parsed, "dd MMM yyyy") : placeholder}
                        </span>
                        {parsed && (
                            <span
                                role="button"
                                tabIndex={0}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onChange("");
                                }}
                                className="shrink-0 opacity-60 hover:opacity-100 cursor-pointer"
                                data-testid={testid ? `${testid}-clear` : undefined}
                            >
                                <X size={11} />
                            </span>
                        )}
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                        mode="single"
                        selected={parsed ?? undefined}
                        onSelect={pick}
                        initialFocus
                    />
                </PopoverContent>
            </Popover>
        </div>
    );
}
